import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import FloatingWindow from "./FloatingWindow";
import WindowTaskbar from "./WindowTaskbar";
import WindowNavigationGuard from "./WindowNavigationGuard";
import { useWindowManager } from "./WindowManagerContext";
import { useResponsive } from "../../hooks/useResponsive";
import GlobalerHinweisToast from "../calendar/globalerHinweis";
import { ZurueckziehenZiel } from "./browserFenster/ZurueckziehenZiel";

const useStyles = makeStyles({
  layer: {
    // Layer wird als absolut positionierter Overlay innerhalb des
    // Main-Content-Wrappers in NavigationLayout gemountet. Damit:
    // - exakt die Bounds des Content-Bereichs (egal ob Sidebar 48px
    //   collapsed oder NavDrawer expandiert ist)
    // - außerhalb des scrollbaren <main> → Taskbar bleibt fix unten
    // - bedeckt nicht App-Header oder Sidebar
    // pointer-events:none am Container, Fenster + Taskbar setzen sich
    // selbst auf auto. Klicks außerhalb der Fenster fließen weiter zum
    // Page-Content darunter.
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: 9000,
  },
});

const WindowLayer: React.FC = () => {
  const styles = useStyles();
  const { windows, registerLayer } = useWindowManager();
  const messRef = React.useRef<HTMLDivElement | null>(null);
  const messAnmelden = React.useCallback(
    (el: HTMLDivElement | null) => {
      messRef.current = el;
      registerLayer(el);
    },
    [registerLayer],
  );
  // T-27: Mess-Element mit exakt den Maßen des Layers. Es ist IMMER gemountet
  // (auch ohne offene Fenster), damit schon das erste Fenster gegen die echte
  // Fläche unter der App-Kopfzeile geöffnet wird — nicht gegen den höheren
  // Browser-Viewport. Unsichtbar, ohne z-index und ohne Zeigerereignisse.
  const messElement = (
    <div
      ref={messAnmelden}
      aria-hidden
      data-testid="fenster-layer-mass"
      style={{ position: "absolute", inset: 0, visibility: "hidden", pointerEvents: "none" }}
    />
  );
  const { isPhoneOrTablet } = useResponsive();

  // Nur sichtbar wenn überhaupt etwas offen ist — sonst kein DOM-Overhead.
  // Der NavigationGuard wird trotzdem gerendert, da er nur dann etwas tut
  // wenn es offene Fenster gibt — aber er muss die Route-Änderungen
  // detektieren können, auch nachdem das letzte Fenster geschlossen wurde.
  // K3: der globale Hinweis-Toast hängt HIER und nicht an einem Fenster —
  // eine fehlgeschlagene Verknüpfung soll auch dann sichtbar werden, wenn das
  // auslösende Fenster sich inzwischen selbst geschlossen hat. Der WindowLayer
  // ist über NavigationLayout immer gemountet und liegt über allen Fenstern.
  if (windows.length === 0) {
    return (
      <>
        {messElement}
        <WindowNavigationGuard />
        <GlobalerHinweisToast />
      </>
    );
  }

  // Phone & Tablet (≤1024px, G2): KEINE schwebenden, ziehbaren Fenster. Jedes
  // Fenster wird als Vollbild-Panel (inset:0) gerendert — alle bleiben gemountet
  // (Form-State!), gestapelt nach zIndex, sodass das aktive (zuletzt
  // fokussierte/geöffnete) Fenster ganz oben sichtbar ist. Minimierte Fenster
  // blendet FloatingWindow via display:none aus und sind über die Taskbar
  // wiederherstellbar. (Nur der Fenster-Modus ist betroffen; das übrige Layout
  // bleibt auf dem Tablet desktop-nah.)
  if (isPhoneOrTablet) {
    return (
      <>
        <div className={styles.layer} aria-label="Fenster-Layer">
          {windows.map((w) => (
            <FloatingWindow key={w.id} window={w} fullscreen />
          ))}
          <WindowTaskbar />
        </div>
        {messElement}
        <WindowNavigationGuard />
        <GlobalerHinweisToast />
      </>
    );
  }

  return (
    <>
      <div className={styles.layer} aria-label="Fenster-Layer">
        {windows.map((w) => (
          <FloatingWindow key={w.id} window={w} />
        ))}
        <WindowTaskbar />
      </div>
      {messElement}
      {/* Nimmt die Kopfleiste eines ausgelagerten Fensters an, die aufs Hauptfenster gezogen wird. */}
      <ZurueckziehenZiel layer={() => messRef.current} />
      <WindowNavigationGuard />
      <GlobalerHinweisToast />
    </>
  );
};

export default WindowLayer;
